import {
  goalInboxID,
  ID,
  newTaskSuffix,
  rootId as defaultRootId,
  SortingMode,
  Task,
  TaskListItemTypes,
  TaskListTreeItem,
  Timestamp,
  WeekDays,
} from '@griply/types';

import { getSortedTasks } from '../get-sorted-tasks';
import { filterEmptyChildrenCascading } from './filter-empty-children-cascading';

export type BuildGoalTasksTreeOptions = {
  goals: { id: ID; name: string }[];
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNoGoalGroup?: boolean;
  hideNewTaskRow?: boolean;
  rootId?: ID;
  inboxLabel: string;
  rootLabel: string;
  newTaskLabel: string;
};

export const buildGoalTasksTree = ({
  goals,
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNoGoalGroup,
  hideNewTaskRow,
  rootId = defaultRootId,
  inboxLabel,
  rootLabel,
  newTaskLabel,
}: BuildGoalTasksTreeOptions): Record<ID, TaskListTreeItem> => {
  const goalTreeItems = goals.reduce<Record<ID, TaskListTreeItem>>(
    (acc, goal) => {
      acc[goal.id] = {
        id: goal.id,
        type: TaskListItemTypes.Goal,
        name: goal.name,
        children: [],
        isFolder: true,
      };
      return acc;
    },
    {},
  );

  if (!hideNoGoalGroup) {
    goalTreeItems[goalInboxID] = {
      id: goalInboxID,
      type: TaskListItemTypes.Goal,
      name: inboxLabel,
      children: [],
      isFolder: true,
    };
  }

  const customTaskOrder =
    sortingMode === SortingMode.Custom
      ? Object.keys(goalTreeItems).reduce<ID[]>((acc, goalId) => {
          const order = customOrder?.[goalId] ?? [];
          acc.push(...order);
          return acc;
        }, [])
      : [];

  const allTreeItems = getSortedTasks(tasks, {
    today,
    weekStartsOn,
    order: customTaskOrder,
    mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
  }).reduce<Record<ID, TaskListTreeItem>>((acc, task) => {
    const directGoalId = task.goalId ?? goalInboxID;
    const parentId = acc[directGoalId] ? directGoalId : goalInboxID;
    const parent = acc[parentId];
    if (parent) {
      parent.children?.push(task.id);
      acc[task.id] = {
        id: task.id,
        name: task.name,
        type: TaskListItemTypes.Task,
      };
    }
    return acc;
  }, goalTreeItems);

  if (!hideNewTaskRow) {
    Object.keys(goalTreeItems).forEach((goalId) => {
      const parent = allTreeItems[goalId];
      if (!parent) {
        return;
      }
      if (!parent.children?.length && !showEmptyGroups) {
        return;
      }

      const newTaskItemId = `${goalId}${newTaskSuffix}`;
      parent.children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: newTaskLabel,
        type: TaskListItemTypes.NewTask,
      };
    });
  }

  const filtered = filterEmptyChildrenCascading(allTreeItems);
  const topLevelIds = Object.keys(filtered).filter(
    (id) => filtered[id].type === TaskListItemTypes.Goal,
  );

  return {
    [rootId]: {
      id: rootId,
      name: rootLabel,
      type: TaskListItemTypes.Root,
      children: topLevelIds,
    },
    ...filtered,
  };
};
